// ActivityItem: avatar (or a small status glyph), one line of plain text, relative time (DESIGN §2).
// Used by the Overview "Today" panel, batch activity, notifications.
import './activity-item.css';
import { diffDays, formatDate, isoDateOfTimestamp } from '../../shared/dates.js';
import { Icon } from '../icons.jsx';
import { Avatar, cx } from './ui.jsx';

// '2026-09-25T10:04:00Z' → 'Just now' | '12 min ago' | '3 h ago' | 'Yesterday' | 'Sep 21'.
// A date-only 'YYYY-MM-DD' compares with `today`: 'Today' | 'Tomorrow' | 'Yesterday' | 'Sep 21'.
export function relativeTime(value, { now = Date.now(), today } = {}) {
  const s = String(value || '');
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    if (today) {
      const days = Math.round((Date.parse(`${s}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000);
      if (days === 0) return 'Today';
      if (days === 1) return 'Tomorrow';
      if (days === -1) return 'Yesterday';
    }
    return formatDate(s);
  }
  const t = Date.parse(s);
  if (Number.isNaN(t)) return '';
  const min = Math.floor((now - t) / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h ago`;
  // Older: the calendar day in the viewer's time zone (an early-morning WIB entry is not the previous UTC day).
  const day = isoDateOfTimestamp(s);
  if (diffDays(day, isoDateOfTimestamp(new Date(now).toISOString())) === 1) return 'Yesterday';
  return formatDate(day);
}

// name: actor (avatar initials). icon + tone: a glyph instead of the avatar (e.g. 'clock' + 'danger' for a deadline).
// text: one line (truncates). at: ISO timestamp or date. meta: replaces the relative time. href: row is a link.
// unread: small lime dot. today: for date-only `at`.
export function ActivityItem({ name, icon, tone, text, at, meta, href, unread, today, onClick, class: cls }) {
  const Tag = href ? 'a' : onClick ? 'button' : 'div';
  const when = meta ?? relativeTime(at, { today });
  return (
    <Tag class={cx('activity', (href || onClick) && 'is-link', unread && 'is-unread', cls)} href={href} type={Tag === 'button' ? 'button' : undefined} onClick={onClick}>
      {icon
        ? <span class={cx('activity-glyph', tone && `tone-${tone}`)}><Icon name={icon} size={14} /></span>
        : <Avatar name={name} size={28} />}
      <span class="activity-body">
        <span class="activity-text" title={typeof text === 'string' ? text : undefined}>{text}</span>
        {when && <span class="activity-time">{when}</span>}
      </span>
      {unread && <span class="activity-unread" aria-label="Unread" />}
    </Tag>
  );
}
